import { useMemo } from "react";
import { format } from "date-fns";
import { getHistoryServices } from "../../../../../common/network/Api";
import { HistoryServiceListItem } from "../../../entities/history";
import { usePnoeCatalogs } from "../../orders/usePnoeMeasurements";
import {
  AssessmentHistoryRow,
  AssessmentKind,
} from "../assessmentHistory";
import useAssessmentHistory from "../useAssessmentHistory";
import useHistoryList from "../useHistoryList";
import { HistoryScope } from "../useHistoryScope";
import { buildServiceLanes, TimelineServiceLane } from "./serviceLanes";
import { TimelineWindow, toDayCenter } from "./timeScale";

/**
 * The events half of the Timeline: test dots and service bars.
 *
 * Nothing new is fetched. Tests are the Assessments tab's merged listing
 * (`useAssessmentHistory`) and services the Programming tab's
 * (`/history/services`, same `useHistoryList` cache key), so switching tabs
 * reuses what is already loaded.
 */

/** The Events & Services view's Tests & Labs rows, in design order. DEXA has
 *  no source yet, so it has no row. */
export const TEST_ROWS: { kind: AssessmentKind; label: string }[] = [
  { kind: "pnoe", label: "PNOE" },
  { kind: "inbody", label: "InBody" },
  { kind: "lab", label: "Labs" },
];

export interface TimelineTestEvent {
  id: string;
  kind: AssessmentKind;
  t: number;
  title: string;
  /** "PNOE test" / "InBody test" / "Lab panel" — the Assessments Type column. */
  category: string;
}

/** Same-day tests share one marker; the combined row shows a count badge. */
export interface TimelineTestCluster {
  key: string;
  t: number;
  events: TimelineTestEvent[];
}

/**
 * When the test was taken. The Assessments table dates InBody by import and
 * labs by order creation; the timeline wants the day of the test itself when
 * the record carries it.
 */
const testTime = (row: AssessmentHistoryRow): number | null => {
  if (row.kind === "inbody") return toDayCenter(row.raw?.testDate) ?? toDayCenter(row.date);
  if (row.kind === "lab") return toDayCenter(row.raw?.sampleDate) ?? toDayCenter(row.date);
  return toDayCenter(row.date);
};

export const clusterByDay = (
  events: TimelineTestEvent[]
): TimelineTestCluster[] => {
  const byDay = new Map<string, TimelineTestEvent[]>();
  [...events]
    .sort((a, b) => a.t - b.t)
    .forEach((event) => {
      const day = format(new Date(event.t), "yyyy-MM-dd");
      byDay.set(day, [...(byDay.get(day) ?? []), event]);
    });
  return Array.from(byDay.entries()).map(([day, dayEvents]) => ({
    key: day,
    t: dayEvents[0].t,
    events: dayEvents,
  }));
};

export interface TimelineEventsResult {
  tests: TimelineTestEvent[];
  lanes: TimelineServiceLane[];
  testsLoading: boolean;
  servicesLoading: boolean;
  /** PNOĒ tests are not available on the customer's own page. */
  pnoeUnavailable: boolean;
}

const useTimelineEvents = (
  scope: HistoryScope,
  window: TimelineWindow
): TimelineEventsResult => {
  const catalogs = usePnoeCatalogs();
  const assessments = useAssessmentHistory(scope, catalogs);
  const services = useHistoryList<HistoryServiceListItem>({
    fetchPage: getHistoryServices,
    cacheKey: "services",
    userid: scope.kind === "company" ? undefined : scope.subjectId,
    enabled: Boolean(scope.subjectId),
  });

  const allTests = useMemo(
    () =>
      assessments.rows
        .map((row): TimelineTestEvent | null => {
          const t = testTime(row);
          if (t === null) return null;
          return {
            id: `${row.kind}:${row.id}`,
            kind: row.kind,
            t,
            title: row.detail || row.typeLabel,
            category: row.typeLabel,
          };
        })
        .filter((event): event is TimelineTestEvent => event !== null),
    [assessments.rows]
  );

  const tests = useMemo(
    () => allTests.filter((e) => e.t >= window.startMs && e.t < window.endMs),
    [allTests, window.startMs, window.endMs]
  );

  const lanes = useMemo(
    () => buildServiceLanes(services.rows, window),
    [services.rows, window]
  );

  return {
    tests,
    lanes,
    testsLoading: assessments.loading,
    servicesLoading: services.loading,
    pnoeUnavailable: assessments.partial,
  };
};

export default useTimelineEvents;
